import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { Playlist, PlaylistModule, PlaylistBonus } from '@/store/playlist/types';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Plus, Upload } from 'lucide-react';
import { nanoid } from 'nanoid';
import { handleImageUploadWithCompression } from '@/utils/compressedImageUpload';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { SortableModuleCard } from '../editor/SortableModuleCard';
import { SortableBonusCard } from '../editor/SortableBonusCard';

interface DarkMembersEditorProps {
  playlist: Partial<Playlist>;
  onUpdate: <K extends keyof Playlist>(key: K, value: Playlist[K]) => void;
}

export function DarkMembersEditor({ playlist, onUpdate }: DarkMembersEditorProps) {
  useLanguage();
  // Defaults aplicados primeiro, depois sobrescritos pelos valores salvos
  const defaultSettings = {
    heroImage: '',
    heroTitle: '',
    heroSubtitle: '',
    showHeroText: false,
    modulesSectionTitle: 'Módulos Iniciais',
    modulesSectionDescription: 'Acesse todos os módulos do curso e comece sua jornada de aprendizado.',
    modules: [] as PlaylistModule[],
    bonusesSectionTitle: 'Bônus Exclusivos',
    bonusesSectionDescription: 'Materiais extras para acelerar seus resultados.',
    bonuses: [] as PlaylistBonus[],
    cardBorderRadius: 16 as (0 | 16),
    cardShadow: true,
    cardOverlay: true,
    cardTextColor: 'white' as ('white' | 'gray'),
    cardTitlePosition: 'inside' as ('none' | 'below' | 'inside'),
    cardBorder: true,
    cardBorderColor: 'rgba(232, 160, 191, 0.4)',
  };

  const savedSettings = (playlist.darkMembersSettings || {}) as Partial<typeof defaultSettings>;

  // Remover propriedades undefined para que o spread não sobrescreva defaults
  const cleanedSettings = Object.fromEntries(
    Object.entries(savedSettings).filter(([_, value]) => value !== undefined)
  ) as Partial<typeof defaultSettings>;

  const settings = {
    ...defaultSettings,
    ...cleanedSettings,
    // Garantir que arrays não sejam sobrescritos por defaults vazios
    modules: savedSettings.modules || [],
    bonuses: savedSettings.bonuses || [],
  };

  const updateSettings = (updates: Partial<typeof settings>) => {
    onUpdate('darkMembersSettings', { ...settings, ...updates });
  };

  const handleHeroImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUploadWithCompression(
        file,
        (compressed) => updateSettings({ heroImage: compressed }),
        { maxWidth: 1200, quality: 0.6 }
      );
    }
  };

  const handleModuleImageUpload = (moduleId: string, file: File) => {
    handleImageUploadWithCompression(
      file,
      (compressed) => updateModule(moduleId, { thumbnailUrl: compressed }),
      { maxWidth: 400, quality: 0.65 }
    );
  };

  const handleBonusImageUpload = (bonusId: string, file: File) => {
    handleImageUploadWithCompression(
      file,
      (compressed) => updateBonus(bonusId, { thumbnailUrl: compressed }),
      { maxWidth: 400, quality: 0.65 }
    );
  };

  const addModule = () => {
    if (settings.modules.length >= 6) return;
    const newModule = {
      id: nanoid(),
      order: settings.modules.length + 1,
      title: `Módulo ${settings.modules.length + 1}`,
      thumbnailUrl: '',
      isActive: true,
      lessons: []
    };
    updateSettings({ modules: [...settings.modules, newModule] });
  };

  const addBonus = () => {
    if (settings.bonuses.length >= 6) return;
    const newBonus = {
      id: nanoid(),
      order: settings.bonuses.length + 1,
      title: `Bônus ${settings.bonuses.length + 1}`,
      thumbnailUrl: '',
      isActive: true
    };
    updateSettings({ bonuses: [...settings.bonuses, newBonus] });
  };

  const updateModule = (moduleId: string, updates: any) => {
    const updatedModules = settings.modules.map(m =>
      m.id === moduleId ? { ...m, ...updates } : m
    );
    updateSettings({ modules: updatedModules });
  };

  const deleteModule = (moduleId: string) => {
    updateSettings({ modules: settings.modules.filter(m => m.id !== moduleId) });
  };

  const updateBonus = (bonusId: string, updates: any) => {
    const updatedBonuses = settings.bonuses.map(b =>
      b.id === bonusId ? { ...b, ...updates } : b
    );
    updateSettings({ bonuses: updatedBonuses });
  };

  const deleteBonus = (bonusId: string) => {
    updateSettings({ bonuses: settings.bonuses.filter(b => b.id !== bonusId) });
  };

  // Configurar sensores de drag
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Mínimo de 8px para iniciar drag
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handler de reordenação de módulos
  const handleModuleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const oldIndex = settings.modules.findIndex((m) => m.id === active.id);
      const newIndex = settings.modules.findIndex((m) => m.id === over.id);
      
      const reorderedModules = arrayMove(settings.modules, oldIndex, newIndex).map(
        (module, idx) => ({ ...module, order: idx + 1 })
      );
      
      updateSettings({ modules: reorderedModules });
    }
  };

  // Handler de reordenação de bônus
  const handleBonusDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const oldIndex = settings.bonuses.findIndex((b) => b.id === active.id);
      const newIndex = settings.bonuses.findIndex((b) => b.id === over.id);
      
      const reorderedBonuses = arrayMove(settings.bonuses, oldIndex, newIndex).map(
        (bonus, idx) => ({ ...bonus, order: idx + 1 })
      );
      
      updateSettings({ bonuses: reorderedBonuses });
    }
  };

  return (
    <div className="space-y-6">
      {/* Configurações das Seções */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">{translateInterfaceText("Configurações das Seções")}</h3>
        
        {/* Seção Módulos */}
        <div className="space-y-4 mb-6">
          <h4 className="text-sm font-medium text-muted-foreground">{translateInterfaceText("Seção de Módulos")}</h4>
          <div>
            <Label>{translateInterfaceText("Título da Seção")}</Label>
            <Input
              value={settings.modulesSectionTitle || ''}
              onChange={(e) => updateSettings({ modulesSectionTitle: e.target.value })}
              placeholder={translateInterfaceText("Ex: Módulos Iniciais")}
            />
          </div>
          <div>
            <Label>{translateInterfaceText("Descrição (opcional)")}</Label>
            <Input
              value={settings.modulesSectionDescription || ''}
              onChange={(e) => updateSettings({ modulesSectionDescription: e.target.value })}
              placeholder={translateInterfaceText("Ex: Acesse todos os módulos do curso...")}
            />
          </div>
        </div>

        <Separator className="my-4" />

        {/* Seção Bônus */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-muted-foreground">{translateInterfaceText("Seção de Bônus")}</h4>
          <div>
            <Label>{translateInterfaceText("Título da Seção")}</Label>
            <Input
              value={settings.bonusesSectionTitle || ''}
              onChange={(e) => updateSettings({ bonusesSectionTitle: e.target.value })}
              placeholder={translateInterfaceText("Ex: Bônus Exclusivos")}
            />
          </div>
          <div>
            <Label>{translateInterfaceText("Descrição (opcional)")}</Label>
            <Input
              value={settings.bonusesSectionDescription || ''}
              onChange={(e) => updateSettings({ bonusesSectionDescription: e.target.value })}
              placeholder={translateInterfaceText("Ex: Materiais extras para acelerar seus resultados")}
            />
          </div>
        </div>
      </Card>

      {/* Capa Principal (Hero) */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">{translateInterfaceText("Capa Principal (Hero)")}</h3>
        <div className="space-y-4">
          <div>
            <Label>{translateInterfaceText("Imagem de Capa (1920×700px recomendado)")}</Label>
            <div className="mt-2">
              {settings.heroImage ? (
                <div className="relative">
                  <img 
                    src={settings.heroImage} 
                    alt={translateInterfaceText("Hero")}
                    className="w-full h-48 object-cover rounded-lg"
                  />
                  <Button
                    size="sm"
                    variant="destructive"
                    className="absolute top-2 right-2"
                    onClick={() => updateSettings({ heroImage: '' })}
                  >
                    {translateInterfaceText("Remover")}</Button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <Upload className="w-10 h-10 mb-3 text-gray-400" />
                    <p className="text-sm text-gray-500">{translateInterfaceText("Clique para fazer upload")}</p>
                  </div>
                  <input 
                    type="file" 
                    className="hidden" 
                    accept="image/*"
                    onChange={handleHeroImageUpload}
                  />
                </label>
              )}
            </div>
          </div>
          
          <Separator className="my-4" />
          
          {/* Toggle Texto Sobreposto */}
          <div className="flex items-center justify-between">
            <Label>{translateInterfaceText("Exibir texto sobre o banner")}</Label>
            <Switch
              checked={settings.showHeroText || false}
              onCheckedChange={(checked) => updateSettings({ showHeroText: checked })}
            />
          </div>
          
          {/* Campos de texto (aparecem se toggle ativo) */}
          {settings.showHeroText && (
            <>
              <div>
                <Label>{translateInterfaceText("Título")}</Label>
                <Input
                  value={settings.heroTitle || ''}
                  onChange={(e) => updateSettings({ heroTitle: e.target.value })}
                  placeholder={translateInterfaceText("Ex: Bem-vindo ao Curso")}
                />
              </div>
              <div>
                <Label>{translateInterfaceText("Subtítulo")}</Label>
                <Input
                  value={settings.heroSubtitle || ''}
                  onChange={(e) => updateSettings({ heroSubtitle: e.target.value })}
                  placeholder={translateInterfaceText("Ex: Comece sua jornada de transformação")}
                />
              </div>
            </>
          )}
        </div>
      </Card>

      {/* Aparência dos Cards */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">{translateInterfaceText("Aparência dos Cards")}</h3>
        <div className="space-y-4">
          <div>
            <Label>{translateInterfaceText("Arredondamento dos Cards")}</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={settings.cardBorderRadius ?? 16}
              onChange={(e) => updateSettings({ cardBorderRadius: Number(e.target.value) as (0 | 16) })}
            >
              <option value={16}>{translateInterfaceText("Arredondado (16px)")}</option>
              <option value={0}>{translateInterfaceText("Sem arredondamento (0px)")}</option>
            </select>
          </div>
          
          <div className="flex items-center justify-between">
            <Label>{translateInterfaceText("Sombra nos cards")}</Label>
            <Switch
              checked={settings.cardShadow ?? true}
              onCheckedChange={(checked) => updateSettings({ cardShadow: checked })}
            />
          </div>
          
          <div className="flex items-center justify-between">
            <Label>{translateInterfaceText("Overlay escuro para leitura")}</Label>
            <Switch
              checked={settings.cardOverlay ?? true}
              onCheckedChange={(checked) => updateSettings({ cardOverlay: checked })}
            />
          </div>
          
          <div>
            <Label>{translateInterfaceText("Cor do texto no overlay")}</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={settings.cardTextColor || 'white'}
              onChange={(e) => updateSettings({ cardTextColor: e.target.value as ('white' | 'gray') })}
            >
              <option value="white">{translateInterfaceText("Branco")}</option>
              <option value="gray">{translateInterfaceText("Cinza claro")}</option>
            </select>
          </div>
          
          <div>
            <Label>{translateInterfaceText("Posição do Título")}</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={settings.cardTitlePosition || 'inside'}
              onChange={(e) => updateSettings({ cardTitlePosition: e.target.value as ('none' | 'below' | 'inside') })}
            >
              <option value="inside">{translateInterfaceText("Texto dentro da imagem (com overlay)")}</option>
              <option value="below">{translateInterfaceText("Texto abaixo da imagem")}</option>
              <option value="none">{translateInterfaceText("Sem nome (apenas imagem)")}</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Gerenciar Módulos */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{translateInterfaceText("Módulos (até 6)")}</h3>
          <Button 
            onClick={addModule} 
            size="sm"
            disabled={settings.modules.length >= 6}
            className={settings.modules.length >= 6 ? 'opacity-50 cursor-not-allowed' : ''}
          >
            <Plus className="w-4 h-4 mr-2" />
            {translateInterfaceText("Adicionar Módulo")}</Button>
        </div>
        
        {settings.modules.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-sm text-gray-500">
              {translateInterfaceText("Nenhum módulo criado. Clique em \"Adicionar Módulo\" para começar.")}</p>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleModuleDragEnd}
          >
            <SortableContext
              items={settings.modules.map((m) => m.id)}
              strategy={rectSortingStrategy}
            >
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {settings.modules.map((module, index) => (
                  <SortableModuleCard
                    key={module.id}
                    module={module}
                    index={index}
                    onUpdate={updateModule}
                    onDelete={deleteModule}
                    onImageUpload={handleModuleImageUpload}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </Card>

      {/* Gerenciar Bônus */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{translateInterfaceText("Bônus (até 6)")}</h3>
          <Button 
            onClick={addBonus} 
            size="sm"
            disabled={settings.bonuses.length >= 6}
            className={settings.bonuses.length >= 6 ? 'opacity-50 cursor-not-allowed' : ''}
          >
            <Plus className="w-4 h-4 mr-2" />
            {translateInterfaceText("Adicionar Bônus")}</Button>
        </div>
        
        {settings.bonuses.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-sm text-gray-500">
              {translateInterfaceText("Nenhum bônus criado. Clique em \"Adicionar Bônus\" para começar.")}</p>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleBonusDragEnd}
          >
            <SortableContext
              items={settings.bonuses.map((b) => b.id)}
              strategy={rectSortingStrategy}
            >
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {settings.bonuses.map((bonus, index) => (
                  <SortableBonusCard
                    key={bonus.id}
                    bonus={bonus}
                    index={index}
                    onUpdate={updateBonus}
                    onDelete={deleteBonus}
                    onImageUpload={handleBonusImageUpload}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </Card>
    </div>
  );
}
